import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productsAPI, categoriesAPI, uploadAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineArrowLeft,
  HiOutlinePhotograph,
  HiOutlineX,
  HiOutlinePlus,
  HiOutlineTrash,
} from 'react-icons/hi';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [product, setProduct] = useState({
    name: '',
    shortDescription: '',
    description: '',
    category: '',
    price: '',
    compareAtPrice: '',
    sku: '',
    images: [],
    variants: [{ name: 'Default', sku: '', price: '', compareAtPrice: '', stock: '', colorHex: '' }],
    specifications: [],
    tags: [],
    status: 'active',
    isFeatured: false,
    isNew: true,
    isBestSeller: false,
  });

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getById(id);
      const p = response.data.product;
      setProduct({
        ...p,
        category: p.category?._id || '',
        price: p.price?.toString() || '',
        compareAtPrice: p.compareAtPrice?.toString() || '',
        images: (p.images || []).filter(img => img !== null),
        variants: p.variants?.map(v => ({
          ...v,
          price: v.price?.toString() || '',
          compareAtPrice: v.compareAtPrice?.toString() || '',
          stock: v.stock?.toString() || '',
        })) || [{ name: 'Default', sku: '', price: '', compareAtPrice: '', stock: '', colorHex: '' }],
        tags: p.tags || [],
      });
    } catch (error) {
      toast.error('Failed to fetch product');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchProduct();
    }
  }, [isEdit, fetchProduct]);

  const handleChange = (e) => {

    const { name, value, type, checked } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVariantChange = (index, field, value) => {
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { ...v, [field]: value } : v
      ),
    }));
  };

  const addVariant = () => {
    setProduct(prev => ({
      ...prev,
      variants: [...prev.variants, { name: '', sku: '', price: '', compareAtPrice: '', stock: '', colorHex: '' }],
    }));
  };

  const removeVariant = (index) => {
    if (product.variants.length === 1) {
      toast.error('At least one variant is required');
      return;
    }
    setProduct(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const handleSpecChange = (index, field, value) => {
    setProduct(prev => ({
      ...prev,
      specifications: prev.specifications.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  };

  const addSpecification = () => {
    setProduct(prev => ({
      ...prev,
      specifications: [...prev.specifications, { name: '', value: '' }],
    }));
  };

  const removeSpecification = (index) => {
    setProduct(prev => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              const response = await uploadAPI.uploadImage(reader.result, 'products');
              resolve(response.data.data.url);
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const newImages = await Promise.all(uploadPromises);

      setProduct(prev => ({
        ...prev,
        images: [...prev.images, ...newImages.map((url, index) => ({
          url,
          alt: '',
          isMain: prev.images.length === 0 && index === 0
        }))],
      }));
      toast.success('Images uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setProduct(prev => ({
      ...prev,
      images: prev.images.filter((img, i) => i !== index && img !== null),
    }));
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    setProduct(prev => ({
      ...prev,
      tags: tagsString.split(',').map(t => t.trim()).filter(Boolean),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!product.name || !product.category || !product.images.length) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const productData = {
        ...product,
        price: parseFloat(product.price) || 0,
        compareAtPrice: parseFloat(product.compareAtPrice) || 0,
        variants: product.variants.map(v => ({
          ...v,
          price: parseFloat(v.price) || parseFloat(product.price) || 0,
          compareAtPrice: parseFloat(v.compareAtPrice) || 0,
          stock: parseInt(v.stock) || 0,
        })),
      };

      if (isEdit) {
        await productsAPI.update(id, productData);
        toast.success('Product updated successfully');
      } else {
        await productsAPI.create(productData);
        toast.success('Product created successfully');
      }
      navigate('/admin/products');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/products')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <HiOutlineArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">
            {isEdit ? 'Edit Product' : 'Add Product'}
          </h1>
          <p className="text-gray-500">
            {isEdit ? 'Update product information' : 'Create a new product'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="font-semibold mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">Product Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={product.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="Enter product name"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Short Description</label>
                  <textarea
                    name="shortDescription"
                    value={product.shortDescription}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold resize-none"
                    placeholder="Brief product description"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Full Description</label>
                  <textarea
                    name="description"
                    value={product.description}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold resize-none"
                    placeholder="Detailed product description (HTML supported)"
                  />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="font-semibold mb-4">Images *</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {product.images.filter(img => img).map((image, index) => {
                  const imageUrl = typeof image === 'string' ? image : image?.url;
                  if (!imageUrl) return null;
                  return (
                    <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                      <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <HiOutlineX className="w-4 h-4" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-1 rounded">
                          Main
                        </span>
                      )}
                    </div>
                  );
                })}
                <label className="aspect-square border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-luxury-gold transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  {uploading ? (
                    <div className="spinner-small" />
                  ) : (
                    <>
                      <HiOutlinePhotograph className="w-8 h-8 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-2">Add Image</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Variants */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold">Variants</h2>
                <button
                  type="button"
                  onClick={addVariant}
                  className="flex items-center gap-1 text-sm text-luxury-gold hover:underline"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  Add Variant
                </button>
              </div>
              <div className="space-y-4">
                {product.variants.map((variant, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Variant {index + 1}</span>
                      {product.variants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={variant.name}
                        onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                        placeholder="Name (e.g., Red, Large)"
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                      />
                      <input
                        type="text"
                        value={variant.sku}
                        onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                        placeholder="SKU"
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                      />
                      <input
                        type="number"
                        value={variant.price}
                        onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                        placeholder="Price"
                        step="0.01"
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                      />
                      <input
                        type="number"
                        value={variant.compareAtPrice}
                        onChange={(e) => handleVariantChange(index, 'compareAtPrice', e.target.value)}
                        placeholder="Compare Price"
                        step="0.01"
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                      />
                      <input
                        type="number"
                        value={variant.stock}
                        onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                        placeholder="Stock"
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                      />
                      <input
                        type="color"
                        value={variant.colorHex || '#000000'}
                        onChange={(e) => handleVariantChange(index, 'colorHex', e.target.value)}
                        className="h-10 w-full border border-gray-200 rounded-lg cursor-pointer"
                        title="Color (optional)"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Specifications */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold">Specifications</h2>
                <button
                  type="button"
                  onClick={addSpecification}
                  className="flex items-center gap-1 text-sm text-luxury-gold hover:underline"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {product.specifications.map((spec, index) => (
                  <div key={index} className="flex gap-3">
                    <input
                      type="text"
                      value={spec.name}
                      onChange={(e) => handleSpecChange(index, 'name', e.target.value)}
                      placeholder="Name (e.g., Material)"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                    />
                    <input
                      type="text"
                      value={spec.value}
                      onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                      placeholder="Value (e.g., Gold)"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-luxury-gold"
                    />
                    <button
                      type="button"
                      onClick={() => removeSpecification(index)}
                      className="p-2 text-red-500 hover:text-red-700"
                    >
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {product.specifications.length === 0 && (
                  <p className="text-sm text-gray-500">No specifications added yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="font-semibold mb-4">Status</h2>
              <select
                name="status"
                value={product.status}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Organization */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="font-semibold mb-4">Organization</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">Category *</label>
                  <select
                    name="category"
                    value={product.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-2">Tags</label>
                  <input
                    type="text"
                    value={product.tags.join(', ')}
                    onChange={handleTagsChange}
                    placeholder="tag1, tag2, tag3"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate with commas</p>
                </div>
                <div>
                  <label className="block text-sm mb-2">SKU</label>
                  <input
                    type="text"
                    name="sku"
                    value={product.sku}
                    onChange={handleChange}
                    placeholder="Product SKU"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="font-semibold mb-4">Pricing</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">Base Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="price"
                      value={product.price}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-2">Compare at Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="compareAtPrice"
                      value={product.compareAtPrice}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Original price for sale display</p>
                </div>
              </div>
            </div>

            {/* Flags */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="font-semibold mb-4">Product Flags</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={product.isFeatured}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Featured Product</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isNew"
                    checked={product.isNew}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">New Arrival</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isBestSeller"
                    checked={product.isBestSeller}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Best Seller</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/admin/products')}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-luxury-charcoal text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
